import { useMemo } from "react";
import { Blocks, PenTool, Shapes } from "lucide-react";
import type { Furniture, ParamValue, TemplateId } from "../../lib/types";
import { TEMPLATES } from "../../lib/furniture/templates";
import { resolveModel } from "../../lib/furniture/resolve";
import { useDesign } from "../../lib/store";
import { TemplateParamsEditor } from "./TemplateParamsEditor";
import { InspectorSection } from "./InspectorSection";
import { cn } from "../../lib/cn";
import { track } from "../../lib/telemetry";

interface Props {
  selected: Furniture;
  onUpdate: (patch: Partial<Furniture>) => void;
  /** Opens the creator seeded from this placed piece. Canvas-only; the library is left alone. */
  onEditShape: () => void;
  /** Start folded behind its header (mobile inspector). */
  collapsible?: boolean;
}

/**
 * Schema-driven "Shape" controls for a parametric piece: the template's
 * `params` render as segmented chips (enums), steppers (ints), or toggles
 * (bools), and the piece regenerates live as they change. A type selector
 * lets the user swap the template outright (a box → sofa) without
 * re-placing the piece.
 *
 * Custom pieces (a sketched `shape`) explain that width and depth scale the
 * outline, and offer Edit shape. Free-form pieces with no sketch — leftover
 * bakes — get the same note and no button, because opening the creator would
 * rebuild their parts from a single outline.
 */
export function FurnitureShapeSection({
  selected,
  onUpdate,
  onEditShape,
  collapsible = false,
}: Props) {
  const model = useMemo(() => resolveModel(selected), [selected]);
  const editingModulesId = useDesign((s) => s.editingModulesId);
  const setEditingModules = useDesign((s) => s.setEditingModules);
  const sectionProps = {
    testId: "furniture-shape-section",
    icon: <Shapes className="size-3.5" />,
    collapsible,
    defaultOpen: !collapsible,
  };
  if (model.kind === "modular") {
    const editing = editingModulesId === selected.id;
    return (
      <InspectorSection
        {...sectionProps}
        label="Modules"
        summary={`${model.modules.length} ${model.modules.length === 1 ? "module" : "modules"}`}
      >
        <p className="mb-2 text-caption text-ink-500">
          Snap seats, corners, and chaises together to build any sectional.
        </p>
        <button
          type="button"
          onClick={() => setEditingModules(editing ? null : selected.id)}
          data-testid="edit-modules-button"
          className={cn(
            "inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl text-sm font-semibold transition-colors",
            editing
              ? "bg-ink-900 text-white hover:bg-ink-800"
              : "bg-accent-50 text-accent-700 ring-1 ring-accent-200 hover:bg-accent-100"
          )}
        >
          <Blocks className="size-4" />
          {editing ? "Done editing modules" : "Edit modules"}
        </button>
      </InspectorSection>
    );
  }
  if (model.kind === "freeform") {
    const canEdit = (selected.shape?.length ?? 0) >= 3;
    return (
      <InspectorSection {...sectionProps} label="Shape" summary="Custom">
        <p className={cn("text-caption text-ink-500", canEdit && "mb-2")}>
          Width and depth scale this outline.
        </p>
        {canEdit && (
          <button
            type="button"
            onClick={onEditShape}
            data-testid="edit-shape-button"
            className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-accent-50 text-sm font-semibold text-accent-700 ring-1 ring-accent-200 hover:bg-accent-100"
          >
            <PenTool className="size-4" />
            Edit shape
          </button>
        )}
      </InspectorSection>
    );
  }
  function setParam(id: string, _value: ParamValue, resolved: Record<string, ParamValue>) {
    if (model.kind !== "parametric") return;
    onUpdate({ model: { ...model, params: resolved } });
    track("furniture_param_changed", { template: model.template, param: id });
  }

  function setTemplate(next: TemplateId) {
    if (model.kind !== "parametric" || next === model.template) return;
    const tpl = TEMPLATES[next];
    onUpdate({
      model: { kind: "parametric", template: next, params: {} },
      frontKind: tpl.frontKind,
      material: selected.material ?? { id: tpl.defaultMaterial, tint: selected.color },
    });
    track("furniture_template_changed", { from: model.template, to: next });
  }

  return (
    <InspectorSection {...sectionProps} label="Shape" summary={TEMPLATES[model.template].label}>
      <TemplateParamsEditor
        template={model.template}
        params={model.params}
        onTemplateChange={setTemplate}
        onParamChange={setParam}
        header={<span className="text-xs text-ink-500">Type</span>}
      />
    </InspectorSection>
  );
}
